import { path, difference, pick } from 'ramda';
import { startOfISOYear } from 'date-fns';
import { createSelector } from 'reselect';
import {
	COUNTRY_FILTER,
	PROVINCE_STATE_FILTER,
	TYPE_FILTER,
	POSTAL_CODE_FILTER,
	GIFT_DATE_BUCKET_FILTER,
	GIFT_BUCKET_FILTER,
	LAST_GIFT_DATE_GTE_FILTER,
	LAST_GIFT_DATE_LTE_FILTER,
	RECENT_GIFT_MONTH_FILTER,
	UNASSIGNED_DONORS_FILTER,
} from '../fundraisers/fundraiser.initialState';
import { RECENT_GIFT_MONTH_FILTER_OPTIONS } from 'src/utils/recentGiftMonthFilterOptions';

export const getFundraisers = state => state.fundraisers.entities;

export const getUserFundraiserId = state => state.fundraisers.me;

export const getFundraiserIdsFetching = state => state.fundraisers.fetching;

export const getUserFundraiser = createSelector(
	getFundraisers,
	getUserFundraiserId,
	(allFundraisers, id) => allFundraisers[id],
);

export const getFundraisersByIds = (state, ids = []) =>
	Object.values(pick(ids, state.fundraisers.entities));

export const getFundraiser = fundraiserId => state => {
	const fundraisers = getFundraisers(state);
	if (fundraiserId === 'me') {
		fundraiserId = getUserFundraiserId(state);
	}
	return fundraiserId ? fundraisers[fundraiserId] : null;
};

export const getFundraiserIdsNotInStore = (state, ids) =>
	difference(ids, Object.keys(state.fundraisers.entities));

export const getYouAndOtherFundraiserNames = createSelector(
	getUserFundraiserId,
	getFundraisersByIds,
	(id, fundraisers) => {
		return fundraisers.reduce((accumulator, fundraiser) => {
			if (fundraiser._id === id) {
				accumulator = ['You', ...accumulator];
			} else {
				accumulator.push(fundraiser.name);
			}

			return accumulator;
		}, []);
	},
);

export const getUserFiscalYearStart = createSelector(getUserFundraiser, fundraiser =>
	fundraiser ? new Date(fundraiser.year_start_from) : startOfISOYear(new Date()),
);

export const getFilterByKey = (state, key) => getFilterByKeySelectorFactory(key)(state);

const getFilterByKeySelectorFactory = filterKey => {
	return createSelector(getUserFundraiser, userFundraiser => path([filterKey], userFundraiser));
};

// default start and end dates to none
export const getDateFilterByKey = key =>
	createSelector(getFilterByKeySelectorFactory(key), dateFilter =>
		dateFilter ? new Date(dateFilter) : null,
	);

export const getMainProspectFilters = createSelector(
	getFilterByKeySelectorFactory(COUNTRY_FILTER),
	getFilterByKeySelectorFactory(PROVINCE_STATE_FILTER),
	getFilterByKeySelectorFactory(TYPE_FILTER),
	getFilterByKeySelectorFactory(POSTAL_CODE_FILTER),
	getFilterByKeySelectorFactory(GIFT_DATE_BUCKET_FILTER),
	getFilterByKeySelectorFactory(GIFT_BUCKET_FILTER),
	getFilterByKeySelectorFactory(UNASSIGNED_DONORS_FILTER),
	getDateFilterByKey(LAST_GIFT_DATE_GTE_FILTER),
	getDateFilterByKey(LAST_GIFT_DATE_LTE_FILTER),
	(
		countryFilter,
		provinceFilter,
		typeFilter,
		postalCodeFilter,
		giftDateBucketFilter,
		giftBucketFilter,
		// need to make sure this filter is not null, as we need to access its 'value' field.
		unassignedDonorsFilter = {},
		lastGiftDateGteFilter,
		lastGiftDateLteFilter,
	) => {
		return {
			[COUNTRY_FILTER]: countryFilter,
			[PROVINCE_STATE_FILTER]: provinceFilter,
			[TYPE_FILTER]: typeFilter,
			[POSTAL_CODE_FILTER]: postalCodeFilter,
			[GIFT_DATE_BUCKET_FILTER]: giftDateBucketFilter,
			[GIFT_BUCKET_FILTER]: giftBucketFilter,
			...unassignedDonorsFilter.value,
			[LAST_GIFT_DATE_GTE_FILTER]: lastGiftDateGteFilter,
			[LAST_GIFT_DATE_LTE_FILTER]: lastGiftDateLteFilter,
		};
	},
);

export const getRecentGiftProspectFilters = state => {
	return (
		getFilterByKey(state, RECENT_GIFT_MONTH_FILTER) || RECENT_GIFT_MONTH_FILTER_OPTIONS[0].value
	);
};



// WEBPACK FOOTER //
// ./src/redux/fundraisers/fundraiser.selector.js